/**
 * ProjectTeamTile — Dashboard-Kachel "Team im Projekt"
 * Zeigt zugewiesene Personen mit Avatar, Rolle und optionalen Badges.
 *
 * Datenquellen (alle vom Parent reingereicht):
 *   - project.project_manager        → Projektleiter (User-ID)
 *   - project.assigned_users         → Array User-IDs aller zugewiesenen MAs
 *   - users                          → Array aller User (für Lookup von name + role)
 *   - assignmentsToday               → Array von Assignments für heute (für Badge "Heute aktiv")
 *   - accommodations                 → Array Unterkünfte mit assignments[] (für Badge "Im Hotel")
 *
 * Click auf die Karte: öffnet Detail-Dialog mit kompletter sortierter Liste.
 */
import { useMemo, useState } from "react";
import { Users as UsersIcon, Crown, Building2, Hammer, Bed, CalendarCheck, Activity, X, ChevronRight, Plus, UserPlus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { authFetch } from "../../App";
import { toast } from "sonner";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ───
const initialsOf = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Deterministische Farbe aus Hash des Namens (HSL für gute Kontraste)
const colorOf = (key) => {
  if (!key) return { bg: "#475569", fg: "#fff" };
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) & 0xffff;
  const hue = h % 360;
  return { bg: `hsl(${hue}, 55%, 45%)`, fg: "#fff" };
};

const ROLE_LABEL = {
  admin: "Projektleiter",
  projektleiter: "Projektleiter",
  mitarbeiter: "Mitarbeiter",
  nachunternehmer: "Nachunternehmer",
};
const ROLE_RANK = { projektleiter: 0, admin: 0, mitarbeiter: 1, nachunternehmer: 2 };

// Premium Rollen-Farbpalette (HSL-basiert, abgestimmt mit Dark-Mode)
// Jede Rolle: Ring-Farbe für Avatar, Badge-Style, optional Glow
const ROLE_PALETTE = {
  projektleiter: {
    ring: "ring-amber-400/80",
    glow: "shadow-[0_0_0_2px_rgba(251,191,36,0.25),0_4px_12px_-2px_rgba(245,158,11,0.35)]",
    badge: "bg-amber-500/12 text-amber-700 dark:text-amber-200 border border-amber-500/30",
    icon: "text-amber-500",
  },
  admin: {  // alias für projektleiter
    ring: "ring-amber-400/80",
    glow: "shadow-[0_0_0_2px_rgba(251,191,36,0.25),0_4px_12px_-2px_rgba(245,158,11,0.35)]",
    badge: "bg-amber-500/12 text-amber-700 dark:text-amber-200 border border-amber-500/30",
    icon: "text-amber-500",
  },
  mitarbeiter: {
    ring: "ring-blue-400/70",
    glow: "shadow-[0_0_0_1.5px_rgba(96,165,250,0.20),0_2px_8px_-2px_rgba(37,99,235,0.25)]",
    badge: "bg-blue-500/12 text-blue-700 dark:text-blue-200 border border-blue-500/25",
    icon: "text-blue-500",
  },
  nachunternehmer: {
    ring: "ring-violet-400/70",
    glow: "shadow-[0_0_0_1.5px_rgba(167,139,250,0.20),0_2px_8px_-2px_rgba(124,58,237,0.25)]",
    badge: "bg-violet-500/12 text-violet-700 dark:text-violet-200 border border-violet-500/25",
    icon: "text-violet-500",
  },
};
const BADGE_TODAY = "bg-emerald-500/12 text-emerald-700 dark:text-emerald-200 border border-emerald-500/30";
const BADGE_HOTEL = "bg-indigo-500/12 text-indigo-700 dark:text-indigo-200 border border-indigo-500/25";

// ─── Avatar ───
function Avatar({ name, size = 32, role = "mitarbeiter", isPM = false, today = false, title }) {
  const { bg, fg } = colorOf(name || "");
  const pal = ROLE_PALETTE[isPM ? "projektleiter" : role] || ROLE_PALETTE.mitarbeiter;
  return (
    <div
      title={title || name}
      className={`relative flex items-center justify-center font-semibold rounded-full ring-2 ring-offset-2 ring-offset-white dark:ring-offset-[var(--tga-bg-elevated,_#1c1f28)] ${pal.ring} ${pal.glow}`}
      style={{ width: size, height: size, background: bg, color: fg, fontSize: Math.max(10, size * 0.36) }}
    >
      {initialsOf(name)}
      {today && (
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-[var(--tga-bg-elevated,_#1c1f28)]" title="Heute eingeplant" />
      )}
    </div>
  );
}

export default function ProjectTeamTile({ project, users = [], assignmentsToday = [], accommodations = [], onOpenDetail, isAdmin = false, onProjectUpdated }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addUserId, setAddUserId] = useState("");

  // Build team list: Projektleiter + assigned_users (deduped)
  const team = useMemo(() => {
    const userById = new Map(users.map(u => [u.id, u]));
    const pmId = project?.project_manager;
    const assigned = Array.isArray(project?.assigned_users) ? project.assigned_users : [];
    const ids = new Set();
    if (pmId) ids.add(pmId);
    for (const id of assigned) ids.add(id);

    const todaySet = new Set();
    for (const a of assignmentsToday || []) {
      if (a?.user_id) todaySet.add(a.user_id);
    }
    const hotelSet = new Set();
    for (const acc of accommodations || []) {
      const accAssigns = acc?.assignments || [];
      for (const aa of accAssigns) {
        if (aa?.user_id) hotelSet.add(aa.user_id);
      }
    }

    const list = Array.from(ids).map(uid => {
      const u = userById.get(uid);
      // Wenn dieser User der Projektleiter ist → role = "projektleiter" (auch wenn er sonst admin ist)
      const isPM = pmId === uid;
      const baseRole = (u?.role || "").toLowerCase();
      const role = isPM ? "projektleiter" : (baseRole || "mitarbeiter");
      return {
        id: uid,
        name: u?.name || "Unbekannt",
        baseRole,
        role,
        isPM,
        active: u?.is_active !== false,
        badges: {
          today: todaySet.has(uid),
          hotel: hotelSet.has(uid),
        },
      };
    });
    // Sort: PL → MA → NU, dann nach Name
    list.sort((a, b) => {
      const r = (ROLE_RANK[a.role] ?? 99) - (ROLE_RANK[b.role] ?? 99);
      if (r !== 0) return r;
      return String(a.name).localeCompare(String(b.name), "de");
    });
    return list;
  }, [project, users, assignmentsToday, accommodations]);

  const countTotal = team.length;
  const countByRole = useMemo(() => {
    const c = { projektleiter: 0, mitarbeiter: 0, nachunternehmer: 0 };
    for (const m of team) c[m.role] = (c[m.role] || 0) + 1;
    return c;
  }, [team]);

  const handleCardClick = () => {
    if (typeof onOpenDetail === "function") onOpenDetail(team);
    else setOpenDialog(true);
  };

  // ─── Bearbeitungs-Aktionen (Admin) ───
  const patchProject = async (body, successMsg) => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/projects/${project.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Fehler beim Speichern");
      } else {
        toast.success(successMsg);
        if (typeof onProjectUpdated === "function") onProjectUpdated();
      }
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const handleSetPM = async (newPmId) => {
    if (!newPmId || newPmId === project?.project_manager) return;
    // Wenn der neue PM bisher in assigned_users war, drinlassen (oder rausnehmen — Geschmackssache).
    // Wir lassen ihn drin, damit beide Felder konsistent bleiben.
    await patchProject({ project_manager: newPmId }, "Projektleiter aktualisiert");
  };

  const handleAddUser = async () => {
    if (!addUserId) return;
    const current = Array.isArray(project?.assigned_users) ? [...project.assigned_users] : [];
    if (current.includes(addUserId)) {
      toast.info("Bereits zugewiesen");
      setAddUserId("");
      return;
    }
    current.push(addUserId);
    await patchProject({ assigned_users: current }, "Mitarbeiter hinzugefügt");
    setAddUserId("");
  };

  const handleRemoveUser = async (uid) => {
    const current = Array.isArray(project?.assigned_users) ? project.assigned_users.filter(x => x !== uid) : [];
    await patchProject({ assigned_users: current }, "Entfernt");
  };

  // User die noch hinzugefügt werden können (nicht PM, nicht in assigned_users, aktiv)
  const addableUsers = useMemo(() => {
    const pmId = project?.project_manager;
    const assigned = new Set(project?.assigned_users || []);
    return users
      .filter(u => u.is_active !== false && u.id !== pmId && !assigned.has(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "de"));
  }, [users, project]);

  // Alle möglichen Projektleiter-Kandidaten (admin oder projektleiter rolle, aktiv)
  const pmCandidates = useMemo(() => {
    return users
      .filter(u => u.is_active !== false && ["admin", "projektleiter"].includes((u.role || "").toLowerCase()))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "de"));
  }, [users]);

  if (countTotal === 0) {
    return (
      <>
        <div className="card-glass-heavy h-full flex flex-col overflow-hidden">
          <div className="card-header tga-drag-handle cursor-grab active:cursor-grabbing select-none">
            <span className="inline-block opacity-30 text-slate-400 select-none">⋮⋮</span>
            <span className="card-header-icon"><UsersIcon className="w-4 h-4" /></span>
            <span className="card-header-title">Team im Projekt</span>
            {isAdmin && (
              <Button size="sm" className="h-7 px-2.5 text-xs card-header-action" onClick={() => setOpenDialog(true)} data-testid="tile-team-open-empty">
                <UserPlus className="w-3 h-3 mr-1" />Zuweisen
              </Button>
            )}
          </div>
          <div className="card-body flex flex-col items-center justify-center text-center flex-1 gap-1">
            <UsersIcon className="w-7 h-7 text-slate-500" />
            <div className="text-sm text-slate-400">Noch niemand zugewiesen</div>
          </div>
        </div>
        {/* Admin-Dialog auch bei leerem Team — siehe unten */}
        {isAdmin && openDialog && (
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UsersIcon className="w-5 h-5" />Team im Projekt
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-3 p-3 rounded-lg border border-dashed border-amber-300/40 bg-amber-50/40 dark:bg-amber-500/5">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[90px]">Projektleiter:</span>
                  <Select value={project?.project_manager || ""} onValueChange={handleSetPM} disabled={saving}>
                    <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="auswählen..." /></SelectTrigger>
                    <SelectContent>{pmCandidates.map(u => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[90px]">Hinzufügen:</span>
                  <Select value={addUserId} onValueChange={setAddUserId} disabled={saving || addableUsers.length === 0}>
                    <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder={addableUsers.length === 0 ? "Niemand verfügbar" : "Person auswählen..."} /></SelectTrigger>
                    <SelectContent>{addableUsers.map(u => (<SelectItem key={u.id} value={u.id}>{u.name} <span className="text-gray-400 text-[10px] ml-1">({ROLE_LABEL[u.role] || u.role})</span></SelectItem>))}</SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 px-3" onClick={handleAddUser} disabled={!addUserId || saving}><Plus className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // Avatar-Stack (max. 5 sichtbar)
  const stackMax = 5;
  const stack = team.slice(0, stackMax);
  const overflow = Math.max(0, countTotal - stackMax);

  return (
    <>
      <div className="card-glass-heavy h-full flex flex-col overflow-hidden" data-testid="tile-team-card">
        <div className="card-header tga-drag-handle cursor-grab active:cursor-grabbing select-none">
          <span className="inline-block opacity-30 text-slate-400 select-none">⋮⋮</span>
          <span className="card-header-icon"><UsersIcon className="w-4 h-4" /></span>
          <span className="card-header-title">Team im Projekt</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 font-mono mr-1 card-header-action" data-testid="tile-team-count">{countTotal}</span>
          {isAdmin && (
            <button
              type="button"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-blue-500/20 text-blue-300 transition-colors card-header-action"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setOpenDialog(true); }}
              title="Team bearbeiten"
              data-testid="tile-team-edit-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="px-4 py-3.5 text-left flex flex-col gap-3.5 flex-1 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
          onClick={handleCardClick}
          data-testid="tile-team-open"
        >
          {/* Avatar-Stack */}
          <div className="flex items-center">
            <div className="flex items-center" style={{ gap: 0 }}>
              {stack.map((m, idx) => (
                <div key={m.id} style={{ marginLeft: idx === 0 ? 0 : -10, zIndex: stack.length - idx }}>
                  <Avatar
                    name={m.name}
                    size={38}
                    role={m.role}
                    isPM={m.isPM}
                    today={m.badges.today}
                    title={`${m.name} · ${ROLE_LABEL[m.role] || m.role}`}
                  />
                </div>
              ))}
              {overflow > 0 && (
                <div
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 text-xs font-semibold ring-2 ring-white dark:ring-[var(--tga-bg-elevated,_#1c1f28)] ring-offset-0"
                  style={{ marginLeft: -10, zIndex: 0 }}
                >
                  +{overflow}
                </div>
              )}
            </div>
            <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
          </div>

          {/* Rollen-Counts mit Pille-Design */}
          <div className="flex flex-wrap gap-1.5">
            {countByRole.projektleiter > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_PALETTE.projektleiter.badge}`}>
                <Crown className="w-3 h-3" />{countByRole.projektleiter} {countByRole.projektleiter === 1 ? "PL" : "PL"}
              </span>
            )}
            {countByRole.mitarbeiter > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_PALETTE.mitarbeiter.badge}`}>
                <Building2 className="w-3 h-3" />{countByRole.mitarbeiter} {countByRole.mitarbeiter === 1 ? "Mitarbeiter" : "Mitarbeiter"}
              </span>
            )}
            {countByRole.nachunternehmer > 0 && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${ROLE_PALETTE.nachunternehmer.badge}`}>
                <Hammer className="w-3 h-3" />{countByRole.nachunternehmer} {countByRole.nachunternehmer === 1 ? "NU" : "NU"}
              </span>
            )}
          </div>

          {/* Top 1-2 Namen */}
          <div className="text-[11.5px] text-gray-500 dark:text-gray-400 line-clamp-1 font-medium">
            {team.slice(0, 2).map(m => m.name).join(" · ")}
            {countTotal > 2 && (
              <span className="text-gray-400 dark:text-gray-500"> · +{countTotal - 2} weitere</span>
            )}
          </div>
        </button>
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              Team im Projekt
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 font-mono">{countTotal}</span>
            </DialogTitle>
          </DialogHeader>

          {/* Admin-Edit: Projektleiter ändern + Mitarbeiter hinzufügen */}
          {isAdmin && (
            <div className="flex flex-col gap-3 p-3 rounded-lg border border-dashed border-amber-300/40 bg-amber-50/40 dark:bg-amber-500/5">
              {/* Projektleiter setzen */}
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[90px]">Projektleiter:</span>
                <Select value={project?.project_manager || ""} onValueChange={handleSetPM} disabled={saving}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="team-pm-select"><SelectValue placeholder="auswählen..." /></SelectTrigger>
                  <SelectContent>
                    {pmCandidates.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Mitarbeiter hinzufügen */}
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[90px]">Hinzufügen:</span>
                <Select value={addUserId} onValueChange={setAddUserId} disabled={saving || addableUsers.length === 0}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="team-add-select">
                    <SelectValue placeholder={addableUsers.length === 0 ? "Niemand mehr verfügbar" : "Person auswählen..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {addableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} <span className="text-gray-400 text-[10px] ml-1">({ROLE_LABEL[u.role] || u.role})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-8 px-3" onClick={handleAddUser} disabled={!addUserId || saving} data-testid="team-add-btn">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 max-h-[50vh] overflow-auto" data-testid="team-dialog-list">
            {team.map(m => {
              const pal = ROLE_PALETTE[m.isPM ? "projektleiter" : m.role] || ROLE_PALETTE.mitarbeiter;
              return (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200/80 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors">
                  <Avatar name={m.name} size={42} role={m.role} isPM={m.isPM} today={m.badges.today} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{m.name}</span>
                      {!m.active && <Badge variant="secondary" className="text-[10px]">inaktiv</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ${pal.badge}`}>
                        {m.role === "projektleiter" ? <Crown className="w-3 h-3" /> : m.role === "nachunternehmer" ? <Hammer className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                        {ROLE_LABEL[m.role] || m.role}
                      </span>
                      {m.badges.today && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ${BADGE_TODAY}`}>
                          <Activity className="w-3 h-3" /> Heute eingeplant
                        </span>
                      )}
                      {m.badges.hotel && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium ${BADGE_HOTEL}`}>
                          <Bed className="w-3 h-3" /> Im Hotel
                        </span>
                      )}
                    </div>
                  </div>
                  {isAdmin && !m.isPM && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                      onClick={() => handleRemoveUser(m.id)}
                      disabled={saving}
                      title="Aus Projekt entfernen"
                      data-testid={`team-remove-${m.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
            {team.length === 0 && (
              <div className="text-center text-sm text-gray-500 py-8">Noch niemand zugewiesen.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
